import { useCallback, useMemo, useState } from 'react';
import type { DashletLayout, DashletMutation } from './dashletTypes';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_LABEL_INDICATOR } from 'components/DashboardGrid/utils';
import { getDashletConstraints } from 'components/DashboardGrid/dimensions';
import {
  getHTMLConfig,
  useHTMLConfig,
  applyConfig,
} from 'pages/Homepages/hooks/useHTMLConfig';
import { getLayoutRowsFromHeight } from 'pages/Homepages/utils';

export const STATIC_CONTENT_CHART_TYPE = 'html';

type BlockDimensions = {
  w?: number;
  h?: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
};

type Dashlet = {
  id: string;
  layout: DashletLayout;
  config?: {
    content?: any;
    chartType?: string;
  };
};

type Dashboard = {
  id: string;
  styleSettings?: Record<string, unknown>;
};

type UseStaticContentBlockParams = {
  currentDashboard?: Dashboard | null;
  dashlets: Dashlet[];
  getDashletPosition: (dimensions: { w?: number; h?: number }) => {
    x: number;
    y: number;
  };
  createDashletMutation: DashletMutation;
  updateDashletMutation: DashletMutation;
  duplicateDashlet: (args: {
    payload: unknown;
    oldDashlet?: Dashlet;
    dashlets: Dashlet[];
    dashboardId: string;
  }) => Promise<unknown> | void;
};

type MutateStaticContentBlockArgs = {
  dashboardId?: string;
  builderData: any;
  dashletId?: string;
  isDuplicate?: boolean;
};

export const isStaticContentDashlet = (
  dashlet?: {
    config?: { chartType?: string };
  } | null
): boolean => dashlet?.config?.chartType === STATIC_CONTENT_CHART_TYPE;

export const getDynamicBlockDimensions = (
  builderData: any
): BlockDimensions => {
  const { htmlBlockConfig } = getHTMLConfig({
    config: {
      content: builderData,
    },
  });

  if (!htmlBlockConfig) {
    return {};
  }

  const { height, columns } = htmlBlockConfig;
  const gridColumns = columns * 2;

  return {
    ...getDashletConstraints({ area: 'static_content' }),
    w: gridColumns,
    h: getLayoutRowsFromHeight(height),
  };
};

export const useStaticContentBlock = ({
  currentDashboard,
  dashlets,
  getDashletPosition,
  createDashletMutation,
  updateDashletMutation,
  duplicateDashlet,
}: UseStaticContentBlockParams) => {
  // The dashlet currently being edited in the builder
  const [builderDashlet, setBuilderDashlet] = useState<Dashlet | null>(null);
  const staticContentBuilderOpen = builderDashlet !== null;

  const mutateStaticContentBlock = useCallback(
    async ({
      dashboardId,
      builderData,
      dashletId,
      isDuplicate = false,
    }: MutateStaticContentBlockArgs) => {
      if (!dashboardId) {
        return;
      }

      const dimensions = getDynamicBlockDimensions(builderData);
      const oldDashlet = dashlets.find((d) => d.id === dashletId);
      const position = getDashletPosition(dimensions);
      const payload = {
        config: {
          entity_type: 'static_content',
          report_type: 'html',
          chart_type: 'html',
          content: builderData,
        },
        name: DEFAULT_LABEL_INDICATOR,
        layout: {
          i: uuidv4(),
          ...dimensions,
          ...position,
        },
      };

      if (Boolean(dashletId) && !isDuplicate) {
        if (!oldDashlet) {
          return;
        }

        await updateDashletMutation.mutateAsync({
          dashboardId,
          dashletId,
          payload: {
            config: payload.config,
            layout: {
              i: uuidv4(),
              ...dimensions,
              x: oldDashlet.layout.x,
              y: oldDashlet.layout.y,
            },
          },
        });
      } else if (isDuplicate) {
        await duplicateDashlet({
          payload,
          oldDashlet,
          dashlets,
          dashboardId,
        });
      } else {
        await createDashletMutation.mutateAsync({
          dashboardId,
          payload,
        });
      }
    },
    [
      dashlets,
      getDashletPosition,
      createDashletMutation,
      updateDashletMutation,
      duplicateDashlet,
    ]
  );

  const openStaticContentBuilder = useCallback((dashlet: Dashlet) => {
    setBuilderDashlet(dashlet);
  }, []);

  const closeStaticContentBuilder = useCallback(() => {
    setBuilderDashlet(null);
  }, []);

  const { builderContent, htmlBlockConfig } = useHTMLConfig(builderDashlet);

  const handleSaveStaticContent = useCallback(
    (builderData: any) => {
      return mutateStaticContentBlock({
        dashboardId: currentDashboard?.id,
        builderData,
        dashletId: builderDashlet?.id,
      });
    },
    [mutateStaticContentBlock, currentDashboard, builderDashlet]
  );

  const handleSaveAndCloseStaticContent = useCallback(
    (builderData: any) => {
      handleSaveStaticContent(builderData);
      closeStaticContentBuilder();
    },
    [handleSaveStaticContent, closeStaticContentBuilder]
  );

  const defaultColumnSize = useMemo(() => {
    const columnCount =
      builderDashlet?.layout && Math.ceil(builderDashlet.layout.w / 2);
    return columnCount && columnCount > 6 ? 6 : columnCount;
  }, [builderDashlet?.layout]);

  const staticContentBuilderProps = useMemo(
    () => ({
      onCancel: closeStaticContentBuilder,
      onSave: handleSaveStaticContent,
      onSaveAndClose: handleSaveAndCloseStaticContent,
      existing: builderContent
        ? applyConfig(builderContent, htmlBlockConfig, builderDashlet?.layout)
        : undefined,
      defaultColumnSize,
      defaultSettings: currentDashboard?.styleSettings,
      allowRelativeLinks: true,
    }),
    [
      closeStaticContentBuilder,
      handleSaveStaticContent,
      handleSaveAndCloseStaticContent,
      builderContent,
      htmlBlockConfig,
      builderDashlet?.layout,
      defaultColumnSize,
      currentDashboard,
    ]
  );

  return {
    mutateStaticContentBlock,
    openStaticContentBuilder,
    closeStaticContentBuilder,
    staticContentBuilderOpen,
    staticContentBuilderProps,
  };
};
